import bind from 'bind-decorator';
import * as React from 'react';
import { MessageDescriptor } from 'react-intl';
import { Redirect } from 'react-router';

import { Icon } from 'elements';
import { buildSearchURL } from 'search/utils';
import { UI_IS_SM } from 'ui/breakpoints';
import messages from 'ui/components/displayMessages';
import { intl } from 'i18n';

interface SearchBoxIconProps {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    darkBackground: boolean;
}

const SearchBoxIcon: React.SFC<SearchBoxIconProps> = ({onClick, darkBackground}) => (
    <button
        title={intl.formatMessage(messages.uiNavSearchButton)}
        className='show-search-box p-0 mr-3 mr-sm-0'
        onClick={onClick}
    >
        <Icon
            name='search-alt'
            zoom='1.5em'
            fill={darkBackground ? 'white' : '#003E6B'}
            className='show-search-box'
        />
    </button>
);

interface SearchBoxFullProps {
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    inputRef: React.RefObject<HTMLInputElement>;
    searchMessage: MessageDescriptor;
    darkBackground: boolean;
}

const SearchBoxFull: React.SFC<SearchBoxFullProps> = (props: SearchBoxFullProps) => (
    <div className='search-box-full input-group'>
        <input type='text'
               className='search-box-input form-control'
               placeholder={intl.formatMessage(props.searchMessage)}
               aria-label={intl.formatMessage(props.searchMessage)}
               onKeyDown={props.onKeyDown}
               ref={props.inputRef}
        />
        <button
            title={intl.formatMessage(messages.uiNavSearchButton)}
            className='input-group-append'
            onClick={props.onClick}
        >
            <span className='input-group-text text-header bg-transparent'>
                <Icon
                    name='search-alt'
                    zoom='1.3em'
                    fill={props.darkBackground ? 'white' : '#003E6B'}
                />
            </span>
        </button>
    </div>
);

export interface SearchBoxProps {
    searchUrl: string;
    darkBackground: boolean;
    searchParams?: string[];
}

export interface SearchBoxState {
    // When the user enters search keywords and hits enter, we use this to redirect them to the search page
    goToSearchURL: string;
    isMobileView: boolean;
    showFullSearchBar: boolean;
}

export class SearchBox extends React.Component<SearchBoxProps, SearchBoxState> {

    private inputRef: React.RefObject<HTMLInputElement>;

    // This matches Bootstrap's definition of small (sm) breakpoint.
    private mediaQuery = UI_IS_SM;

    constructor(props: SearchBoxProps)  {
        super(props);
        this.inputRef = React.createRef();
        this.state = {
            goToSearchURL: '',
            isMobileView: false,
            showFullSearchBar: false,
        };
    }

    public componentDidMount() {
        this.mediaQuery.addListener(this.setIsMobileView);
        this.setIsMobileView();
    }

    public render() {
        if (this.state.goToSearchURL) {
            return <Redirect to={this.state.goToSearchURL} />;
        }
        return (
            <>
                {this.state.isMobileView && !this.state.showFullSearchBar
                    ? <SearchBoxIcon onClick={this.onClick} darkBackground={this.props.darkBackground} />
                    : <SearchBoxFull
                        onKeyDown={this.onKeyDown}
                        onClick={this.onClick}
                        inputRef={this.inputRef}
                        searchMessage={messages.uiNavSearch}
                        darkBackground={this.props.darkBackground}
                    >
                    </SearchBoxFull>
                }
            </>
        );
    }

    @bind private setIsMobileView(): void {
        const isMobileView = this.mediaQuery.matches;
        this.setState({isMobileView, showFullSearchBar: !isMobileView});
    }

    @bind private onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        const val = event.currentTarget.value.trim();
        if (event.key === 'Enter' && val) {
            event.preventDefault();
            this.onSearchQueryEntered(val);
            event.currentTarget.value = '';
        }
    }

    @bind private onClick(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        if (this.state.isMobileView) {
            this.setState({showFullSearchBar: !this.state.showFullSearchBar});
        } else if (this.inputRef.current) {
            const val = this.inputRef.current.value.trim();
            if (val) {
                this.onSearchQueryEntered(val);
                this.inputRef.current.value = '';
            }
        }
    }

    @bind private onSearchQueryEntered(keyword: string) {
        const newURL = buildSearchURL(this.props.searchUrl, new Set([keyword]),
            new Set(this.props.searchParams));
        this.setState({ goToSearchURL: newURL}, () => {
            // Avoid an infinite loop by clearing this after.
            this.setState({ goToSearchURL: '' });
        });
    }
}
